import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import NavUser from "../components/NavUser";
import { FaCheckCircle, FaCreditCard, FaArrowRight, FaClock } from "react-icons/fa";
import "./Register.css"; // Reuse premium theme
import "./PaymentSuccess.css";

function PaymentSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [booking, setBooking] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [paymentsLkVerified, setPaymentsLkVerified] = useState(false);
  const [paymentsLkPending, setPaymentsLkPending] = useState(false);
  const [transactionRef, setTransactionRef] = useState("");
  const [verifiedAmount, setVerifiedAmount] = useState(null);

  // Parse data from URL params or location state (handles Payments.lk & Koko redirects, with legacy parameter compatibility)
  const isPaymentsLk = searchParams.get("payments_lk") === "true" || searchParams.get("provider") === "payments_lk";
  const plkReference = searchParams.get("reference");
  const orderIdParam = searchParams.get("order_id") || searchParams.get("orderId") || plkReference;
  let type = searchParams.get("type") || location.state?.type;
  let bookingId = searchParams.get("bookingId") || searchParams.get("booking_id") || location.state?.bookingId;
  let amountPaid = searchParams.get("amount") || location.state?.amountPaid;

  if (orderIdParam) {
    if (orderIdParam.startsWith("PLK_") || orderIdParam.startsWith("VISA_") || orderIdParam.includes("_VISA_")) {
      if (orderIdParam.includes("VISA")) type = "visa";
    }
    if (orderIdParam.startsWith("PLK_HOTEL_") || orderIdParam.startsWith("BOOK_") || orderIdParam.startsWith("HOTEL_")) {
      type = "hotel";
      if (!bookingId) {
        const parts = orderIdParam.split("_");
        bookingId = parts[2] || parts[1];
      }
    } else if (orderIdParam.startsWith("PLK_TOUR_") || orderIdParam.startsWith("TOUR_") || orderIdParam.startsWith("GUIDE_")) {
      type = "tour";
      if (!bookingId) {
        const parts = orderIdParam.split("_");
        bookingId = parts[2] || parts[1];
      }
    } else if (orderIdParam.startsWith("PLK_VISA_") || orderIdParam.startsWith("VISA_")) {
      type = "visa";
      if (!bookingId) {
        const parts = orderIdParam.split("_");
        bookingId = parts[2] || parts[1];
      }
    } else if (orderIdParam.startsWith("PLK_EVENT_") || orderIdParam.startsWith("EVT_")) {
      type = "event";
      if (!bookingId) {
        const parts = orderIdParam.split("_");
        bookingId = parts[2] || parts[1] || orderIdParam;
      }
    }
  }

  if (!type) type = "tour";

  useEffect(() => {
    let isMounted = true;
    let pollCount = 0;
    const maxPolls = 5;

    const pollPaymentsLkStatus = async (ref) => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/payment/payments-lk/status/${encodeURIComponent(ref)}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem("jwt_token")}` } }
        );
        if (res.data?.verified) {
          if (isMounted) {
            setPaymentsLkVerified(true);
            setTransactionRef(ref);
            if (res.data.amount) setVerifiedAmount(res.data.amount);
            if (res.data.booking) setBooking(res.data.booking);
            setIsLoading(false);
          }
          return true;
        }
      } catch (err) {
        console.warn("Payments.lk status polling error:", err.message);
      }
      return false;
    };

    const fetchBookingDetails = async () => {
      try {
        // If returning from Payments.lk, poll authoritative payment status
        if (plkReference || isPaymentsLk) {
          const targetRef = plkReference || orderIdParam;
          setTransactionRef(targetRef);

          let verified = false;
          while (pollCount < maxPolls && !verified && isMounted) {
            verified = await pollPaymentsLkStatus(targetRef);
            if (!verified) {
              pollCount++;
              if (pollCount < maxPolls) {
                await new Promise((resolve) => setTimeout(resolve, 1500));
              }
            }
          }

          if (!verified && isMounted) {
            setPaymentsLkPending(true);
          }
        }

        // Fetch display entity for receipt
        if (type === "visa" && bookingId) {
          const res = await axios.get(`${process.env.REACT_APP_API_URL}/visa/status/${bookingId}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("jwt_token")}` },
          });
          if (isMounted) {
            setBooking(
              res.data?.application || {
                id: `ETA-0000${bookingId}`,
                price: amountPaid || 3000,
                paidAmount: amountPaid || 3000,
              }
            );
          }
        } else if (type === "event") {
          if (isMounted) {
            setBooking({
              id: bookingId,
              price: amountPaid || 0,
              paidAmount: amountPaid || 0,
              title: "Event Ticket Booking",
            });
          }
        } else if (bookingId) {
          const endpoint = type === "tour" ? `/tour-bookings/${bookingId}` : `/bookings/${bookingId}`;
          const res = await axios.get(`${process.env.REACT_APP_API_URL}${endpoint}`);
          const data = res.data?.booking || res.data;
          if (isMounted && data) {
            setBooking(data);
          }
        }
      } catch (err) {
        console.error("Failed to fetch booking details:", err);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchBookingDetails();

    return () => {
      isMounted = false;
    };
  }, [bookingId, type, amountPaid, plkReference, isPaymentsLk, orderIdParam]);

  if (isLoading) {
    return (
      <div className="agent-page-premium">
        <NavUser />
        <div className="loader-container">
          <span className="loader-dots">Confirming Payment...</span>
        </div>
      </div>
    );
  }

  const effectivePaid = parseFloat(verifiedAmount || amountPaid || booking?.paidAmount || booking?.price || 0);
  const isFullPayment = booking ? (effectivePaid >= parseFloat(booking.price || 0)) : true;

  return (
    <div className="agent-page-premium">
      <NavUser />
      
      <div className="premium-hero-mini">
        <div className="hero-content">
          <h1>Payment Successful!</h1>
          <p>Your booking has been secured. We've sent a confirmation email to you.</p>
        </div>
      </div>

      <div className="premium-container" style={{ maxWidth: '600px', margin: '0 auto' }}>
        <div className="premium-glass-card success-summary-card" style={{ textAlign: 'center', padding: '40px' }}>
          <div className="success-icon-wrapper">
            <FaCheckCircle className="success-check-icon" />
          </div>
          
          {paymentsLkPending && (
            <div style={{ background: '#fffbeb', border: '1px solid #fef3c7', borderRadius: '12px', padding: '16px', marginBottom: '20px', color: '#92400e', textAlign: 'left', fontSize: '14px' }}>
              <strong><FaClock style={{ marginRight: '6px' }} /> Confirmation in Progress:</strong> Your payment was authorized by Payments.lk and is undergoing asynchronous cryptographic confirmation. Your booking will be finalized automatically.
            </div>
          )}

          <h2 style={{ fontSize: '28px', fontWeight: '800', color: '#1e293b', marginBottom: '10px' }}>
            {paymentsLkPending ? "Payment Received" : "Reservation Confirmed"}
          </h2>
          <p style={{ color: '#64748b', fontSize: '16px', marginBottom: '30px' }}>
            {paymentsLkPending 
              ? "We are verifying your transaction with Payments.lk. A confirmation email will follow shortly."
              : "Thank you for choosing Soloway. Your reservation is now officially confirmed!"}
          </p>

          <div className="payment-details-box" style={{ background: '#f8fafc', borderRadius: '16px', padding: '25px', textAlign: 'left', marginBottom: '30px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
              <span style={{ color: '#64748b', fontWeight: '600' }}>Reference Code</span>
              <span style={{ fontWeight: '800', color: '#1e293b' }}>
                {transactionRef || (type === 'visa'
                  ? (bookingId?.startsWith("ETA-") ? bookingId : `ETA-${String(bookingId || "").padStart(6, "0")}`)
                  : type === 'event'
                  ? (bookingId?.startsWith("EVT") ? bookingId : `EVT-${bookingId}`)
                  : (type === 'tour' ? 'TR-' : 'HT-') + (bookingId || booking?.id))}
              </span>
            </div>
            {paymentsLkVerified && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
                <span style={{ color: '#64748b', fontWeight: '600' }}>Payment Provider</span>
                <span style={{ fontWeight: '700', color: '#0284c7' }}>Payments.lk (Verified)</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
              <span style={{ color: '#64748b', fontWeight: '600' }}>Amount Paid</span>
              <span style={{ fontWeight: '800', color: '#10b981' }}>Rs. {effectivePaid.toLocaleString()}</span>
            </div>
            {booking && type !== 'visa' && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
                  <span style={{ color: '#64748b', fontWeight: '600' }}>Total Price</span>
                  <span style={{ fontWeight: '800', color: '#1e293b' }}>Rs. {booking.price?.toLocaleString()}</span>
                </div>
                {!isFullPayment && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderTop: '1px dashed #cbd5e1', marginTop: '10px' }}>
                    <span style={{ color: '#e53e3e', fontWeight: '700' }}>Remaining Balance</span>
                    <span style={{ fontWeight: '800', color: '#e53e3e' }}>Rs. {(booking.price - booking.paidAmount).toLocaleString()}</span>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="success-actions" style={{ display: 'flex', gap: '15px' }}>
            <button className="btn-v2-secondary" style={{ flex: 1 }} onClick={() => navigate(type === 'visa' ? "/visa" : "/bookings")}>
              {type === 'visa' ? 'View Visa Status' : 'View My Bookings'}
            </button>
            <button className="btn-v2-primary" style={{ flex: 1 }} onClick={() => navigate("/explore")}>
              Explore More <FaArrowRight style={{ marginLeft: '8px' }} />
            </button>
          </div>
        </div>

        <div className="next-steps-section" style={{ marginTop: '40px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '20px' }}>Next Steps</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div className="step-card" style={{ display: 'flex', gap: '15px', alignItems: 'flex-start' }}>
              <FaCreditCard style={{ color: '#3b82f6', fontSize: '20px', marginTop: '4px' }} />
              <div>
                <h4 style={{ fontSize: '15px', fontWeight: '700', marginBottom: '4px' }}>Check Email</h4>
                <p style={{ fontSize: '13px', color: '#64748b' }}>We've sent your receipt and check-in details to your inbox.</p>
              </div>
            </div>
            <div className="step-card" style={{ display: 'flex', gap: '15px', alignItems: 'flex-start' }}>
              <FaClock style={{ color: '#3b82f6', fontSize: '20px', marginTop: '4px' }} />
              <div>
                <h4 style={{ fontSize: '15px', fontWeight: '700', marginBottom: '4px' }}>Prepare Your Trip</h4>
                <p style={{ fontSize: '13px', color: '#64748b' }}>Get ready for your adventure! You can message your host anytime.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaymentSuccess;
